import { onlineManager } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import { registerServiceWorker } from "../../service-worker-register";

interface OfflineContextValue {
  online: boolean | undefined;
  storeData: (key: string, data: unknown) => Promise<unknown>;
  getData: <T extends unknown>(key: string) => Promise<T | undefined>;
  setOnline: React.Dispatch<React.SetStateAction<boolean>>;
  checkOnline: () => Promise<void>;
}

export const OfflineContext = React.createContext<OfflineContextValue>(null!);

export const useOfflineContext = () => useContext(OfflineContext);

interface OfflineProviderProps {
  children: React.ReactNode;
}

export const OfflineProvider = React.memo<OfflineProviderProps>((props) => {
  const { children } = props;

  const [online, setOnline] = useState(true);
  const [locationHref, setLocationHref] = useState<string>();
  onlineManager.setOnline(true);

  useEffect(() => {
    // service worker needs to be registered on a path
    // with a trailing slash because of scope issues
    // https://github.com/w3c/ServiceWorker/issues/1272
    //
    // This is a workaround to redirect the user to the
    // correct path for sw registration
    if (window.location.pathname.endsWith('/')) {
      registerServiceWorker();
      return;
    }

    window.location.href = window.location.pathname + '/';
  }, []);

  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    // on beforeunload, check if the location is valid
    // (ends with a trailing slash and is not the root)
    // if not, redirect to the correct location saved in
    // locationHref otherwise redirect to the
    // [referer](https://developer.mozilla.org/en-US/docs/Web/API/Document/referrer)
    // (in case of manual refresh, referer is the url
    // that was in the address bar before the refresh)
    const locationValid = window.location.pathname.length > 2 && window.location.pathname.endsWith('/');

    if (locationValid) {
      return;
    }

    if (locationHref) {
      window.location.href = window.location.pathname + '/';
    }

    window.location.href = document.referrer + (document.referrer.endsWith('/') ? '' : '/');
  }, [])

  useBeforeUnload(handleBeforeUnload);

  useEffect(() => {
    window.addEventListener('online', checkOnline);
    window.addEventListener('offline', checkOnline);
    checkOnline();
    return () => {
      window.removeEventListener('online', checkOnline);
      window.removeEventListener('offline', checkOnline);
    };
  }, []);

  const checkOnline = useCallback(async () => {
    setLocationHref(() => window.location?.href);

    try {
      const response = await fetch(process.env.API_URL + "/favicon.ico");

      if (!response.ok || response.status === 503) {
        throw new Error('Network not available');
      }

      setOnline(() => true);
    } catch (error) {
      setOnline(() => false);
    }
  }, []);

  const storeData = useCallback(async (key: string, data: unknown) => {
    if (!('caches' in window)) {
      return new Promise((_, reject) => { reject() });
    }

    const cache = await caches.open(process.env.SW_CACHE_NAME);

    await cache.put(key, new Response(JSON.stringify(data)));
  }, []);

  const getData = useCallback(async <T extends unknown>(key: string): Promise<T | undefined> => {
    if (!('caches' in window)) {
      return new Promise((_, reject) => { reject() });
    }

    const cache = await caches.open(process.env.SW_CACHE_NAME);

    const cacheData = await cache.match(key);
    const data = await cacheData?.text();

    if (data) {
      return JSON.parse(data);
    }

    return undefined;
  }, []);

  const value = useMemo(() => ({
    online,
    storeData,
    getData,
    setOnline,
    checkOnline,
  }), [online, storeData]);

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
});

export default OfflineProvider;